import { LoaderCircle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const DEFAULT_FORM = {
  subdomain_slug: "",
  name: "",
  phone: "",
  cea_registration_number: "",
  parent_agency_name: "",
  agency_license_number: "",
  profile_photo_url: "",
  advisor_tagline: "",
  advisor_bio: "",
  xml_syndication_url: "",
  xml_sync_enabled: false,
};

export const buildAgentProfileFormState = (source = {}) => ({
  ...DEFAULT_FORM,
  subdomain_slug: source.subdomain_slug || "",
  name: source.name || "",
  phone: source.phone || "",
  cea_registration_number: source.cea_registration_number || "",
  parent_agency_name: source.parent_agency_name || "",
  agency_license_number: source.agency_license_number || "",
  profile_photo_url: source.profile_photo_url || "",
  advisor_tagline: source.advisor_tagline || "",
  advisor_bio: source.advisor_bio || "",
  xml_syndication_url: source.xml_syndication_url || "",
  xml_sync_enabled: Boolean(source.xml_sync_enabled),
});

export const AgentProfileSetupForm = ({
  busy = false,
  ctaLabel = "Save",
  description,
  form,
  onChange,
  onUploadHeadshot,
  portalPreview,
  title,
}) => {
  return (
    <div className="space-y-6" data-testid="agent-profile-setup-form">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Agent profile</p>
        <h2 className="mt-3 text-2xl font-semibold text-[#001935]" data-testid="agent-profile-setup-form-title">{title}</h2>
        {description ? <p className="mt-3 text-sm leading-7 text-[#42566f]" data-testid="agent-profile-setup-form-description">{description}</p> : null}
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="agent-profile-slug-input">Custom URL slug</Label>
          <Input data-testid="agent-profile-slug-input" id="agent-profile-slug-input" onChange={(event) => onChange("subdomain_slug", event.target.value)} placeholder="e.g. dennis" value={form.subdomain_slug} />
          <p className="text-xs text-[#6b7280]" data-testid="agent-profile-slug-preview">Public route: {portalPreview || "/agent/your-name"}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-profile-name-input">Full Name</Label>
          <Input data-testid="agent-profile-name-input" id="agent-profile-name-input" onChange={(event) => onChange("name", event.target.value)} placeholder="Your full name" value={form.name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-profile-phone-input">Contact Number</Label>
          <Input data-testid="agent-profile-phone-input" id="agent-profile-phone-input" onChange={(event) => onChange("phone", event.target.value)} placeholder="+65 ..." value={form.phone} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-profile-cea-input">CEA Registration Number</Label>
          <Input data-testid="agent-profile-cea-input" id="agent-profile-cea-input" onChange={(event) => onChange("cea_registration_number", event.target.value.toUpperCase())} placeholder="R123456A" value={form.cea_registration_number} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-profile-agency-name-input">Parent Agency Name</Label>
          <Input data-testid="agent-profile-agency-name-input" id="agent-profile-agency-name-input" onChange={(event) => onChange("parent_agency_name", event.target.value)} placeholder="e.g. Huttons Asia" value={form.parent_agency_name} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-profile-agency-license-input">Agency License Number</Label>
          <Input data-testid="agent-profile-agency-license-input" id="agent-profile-agency-license-input" onChange={(event) => onChange("agency_license_number", event.target.value.toUpperCase())} placeholder="L3000001A" value={form.agency_license_number} />
        </div>
      </div>

      <div className="space-y-3 rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-4" data-testid="agent-profile-headshot-panel">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[#001935]">Profile Headshot</p>
            <p className="mt-1 text-xs leading-6 text-[#6b7280]">Upload a portrait image so your public portal and listings reflect your profile properly.</p>
          </div>
          <Button data-testid="agent-profile-upload-headshot-button" disabled={busy} onClick={onUploadHeadshot} type="button" variant="outline">
            <Upload className="mr-2 h-4 w-4" /> Upload Headshot
          </Button>
        </div>
        {form.profile_photo_url ? (
          <div className="overflow-hidden rounded-[22px] border border-[#001935]/10 bg-white p-3" data-testid="agent-profile-headshot-preview-card">
            <div className="flex aspect-[4/5] w-full items-center justify-center rounded-[18px] bg-[linear-gradient(180deg,#fffdf8_0%,#f7f1e4_100%)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]" data-testid="agent-profile-headshot-preview-frame">
              <img alt="Agent headshot preview" className="h-full w-full rounded-[16px] object-contain object-center" data-testid="agent-profile-headshot-preview" src={form.profile_photo_url} />
            </div>
            <p className="mt-3 text-xs leading-6 text-[#6b7280]" data-testid="agent-profile-headshot-preview-hint">
              Portraits are centered with safe padding here so you can review the full headshot before saving.
            </p>
          </div>
        ) : (
          <div className="rounded-[18px] border border-dashed border-[#001935]/20 bg-white/70 px-4 py-5 text-sm text-[#6b7280]" data-testid="agent-profile-headshot-empty-state">
            No headshot uploaded yet.
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-profile-tagline-input">Public Tagline</Label>
        <Input data-testid="agent-profile-tagline-input" id="agent-profile-tagline-input" onChange={(event) => onChange("advisor_tagline", event.target.value)} placeholder="Short public-facing positioning line" value={form.advisor_tagline || ""} />
      </div>

      <div className="space-y-2">
        <Label htmlFor="agent-profile-bio-input">Profile Bio</Label>
        <Textarea data-testid="agent-profile-bio-input" id="agent-profile-bio-input" onChange={(event) => onChange("advisor_bio", event.target.value)} placeholder="Short professional summary" rows={5} value={form.advisor_bio || ""} />
      </div>

      <div className="space-y-3 rounded-[24px] border border-[#001935]/10 bg-[#f8fbff] p-4" data-testid="agent-xml-syndication-panel">
        <div>
          <p className="text-sm font-semibold text-[#001935]">External Feed Connection</p>
          <p className="mt-1 text-xs leading-6 text-[#6b7280]">Connect your Huttons or PropertyGuru-style external feed here. We’ll sync listings to your portfolio and you can also trigger a manual sync anytime.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="agent-profile-xml-syndication-url-input">External Feed URL (XML)</Label>
          <Input data-testid="agent-profile-xml-syndication-url-input" id="agent-profile-xml-syndication-url-input" onChange={(event) => onChange("xml_syndication_url", event.target.value)} placeholder="https://example.com/propertyguru-feed.xml" value={form.xml_syndication_url || ""} />
        </div>
        <label className="flex items-center gap-3 rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#001935]" data-testid="agent-profile-xml-syndication-enabled-toggle">
          <input checked={Boolean(form.xml_sync_enabled)} data-testid="agent-profile-xml-syndication-enabled-checkbox" onChange={(event) => onChange("xml_sync_enabled", event.target.checked)} type="checkbox" />
          Enable daily external feed sync at 2:00 AM SGT
        </label>
      </div>

      <Button className="gold-button w-full justify-center !py-3" data-testid="agent-profile-save-button" disabled={busy} type="submit">
        {busy ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
        {ctaLabel}
      </Button>
    </div>
  );
};

export default AgentProfileSetupForm;